'use client'

import { I18NText } from '@/components/I18NText'
import { useI18N } from '@/context/I18NContext'
import type { TranslationKey } from '@carbonara/translations'
import { COUNTRY_ISO_CODE } from '@carbonara/utils'
import { Controller, useFormContext } from 'react-hook-form'
import { ComboBox } from './ComboBox'
import { FormInput } from './FormsLayout'

interface CountryInputProps {
	name?: string
	disabled?: boolean
	placeholder?: TranslationKey | null
	label?: TranslationKey | null
	className?: string | null
	items?: { label: string; value: string }[]
}

export const CountryInput: React.FC<CountryInputProps> = ({
	disabled = false,
	name = 'country',
	placeholder = 'transactions.details.items.form.country.placeholder',
	label = 'transactions.details.items.form.country.label',
	className = 'min-w-[360px]',
	items: items_,
}) => {
	const {
		dictionary: { countries },
	} = useI18N()
	const countriesList = COUNTRY_ISO_CODE.map((country) => ({
		label: countries[country] || country,
		value: country,
	})).sort((a, b) => (a.label > b.label ? 1 : -1))

	const items = items_ ?? countriesList

	const {
		control,
		formState: { errors },
	} = useFormContext<{ [key: typeof name]: string }>()

	const { getTranslation } = useI18N()

	return (
		<FormInput className={className ?? undefined}>
			<Controller
				control={control}
				name={name}
				disabled={disabled}
				render={({ field }) => (
					<ComboBox
						items={items}
						placeholder={
							placeholder !== null ? getTranslation(placeholder) : undefined
						}
						label={label !== null ? <I18NText text={label} /> : undefined}
						error={
							errors[name] &&
							label && (
								<I18NText
									text="forms.fieldIsRequired"
									values={{
										field: getTranslation(label),
									}}
								/>
							)
						}
						{...field}
					/>
				)}
			/>
		</FormInput>
	)
}
